import React from "react";
import { Provider } from "react-redux";
import { readQueryParams } from "tsi-common-react/src/apps/reviews/history";
import {
    setFacetValues,
    setProductTypes,
} from "tsi-common-react/src/apps/reviews/reducers";
import {
    isoFormUUID,
    isoReviewsBrandID,
    isoReviewsProductTypeID,
    isoWebPageURL,
} from "tsi-common-react/src/models/nominals";
import { ReviewSortOption } from "tsi-common-react/src/models/reviews";
import { UGCListSubBlock } from "tsi-common-react/src/models/ugc";
import { strToBool } from "tsi-common-react/src/utils/format";
import { notEmpty, unique } from "tsi-common-react/src/utils/functional";
import { dynamicPlaceComponent } from "tsi-common-react/src/utils/react";
import { v4 as uuidv4 } from "uuid";

import { store } from "../store";

interface IUGCCarouselData {
    desc: string;
    posts: UGCListSubBlock[];
}

/**
 * Init facet values from the URL
 */

const initialFacetValues = readQueryParams();
if (initialFacetValues.product_type_id) {
    const productIDs = `${initialFacetValues.product_type_id}`
        .split(",")
        .map((ptid) => {
            return ptid
                ? isoReviewsProductTypeID.wrap(parseInt(ptid, 10))
                : null;
        })
        .filter(notEmpty)
        .filter(unique);
    if (productIDs.length) {
        store.dispatch(setProductTypes(productIDs));
    }
}
delete initialFacetValues.product_type_id;
store.dispatch(setFacetValues(initialFacetValues));

/**
 * Render "Write Review" buttons
 */
dynamicPlaceComponent('[data-place-react="write-review-button"]', async () => {
    const { SingleProductCollapsibleWriteReviewForm } = await import(
        "tsi-common-react/src/apps/reviews/containers/SingleProductCollapsibleWriteReviewForm"
    );
    return (
        <Provider store={store}>
            <SingleProductCollapsibleWriteReviewForm
                formUUID={isoFormUUID.wrap(uuidv4())}
            />
        </Provider>
    );
}).catch(console.error);

/**
 * Render single-product write review form
 */
dynamicPlaceComponent('[data-place-react="write-review"]', async (elem) => {
    const { SingleProductReviewSwitch } = await import(
        "tsi-common-react/src/apps/reviews/containers/SingleProductReviewSwitch"
    );
    const brandID = isoReviewsBrandID.wrap(
        parseInt(elem.dataset.reviewsBrandId || "1", 10),
    );
    // TODO(i18n): Do not hardcode brand name fallback - should come from backend or be translatable
    const brand = elem.dataset.brand || "Tempur-Pedic";
    const returnURL = elem.dataset.returnUrl
        ? isoWebPageURL.wrap(elem.dataset.returnUrl)
        : undefined;
    return (
        <Provider store={store}>
            <SingleProductReviewSwitch
                formUUID={isoFormUUID.wrap(uuidv4())}
                brandID={brandID}
                brand={brand}
                returnURL={returnURL}
            />
        </Provider>
    );
}).catch(console.error);

/**
 * Render multi-product write review form
 */
dynamicPlaceComponent(
    '[data-place-react="multi-product-write-review-form"]',
    async (elem) => {
        const { MultiProductWriteReviewForm } = await import(
            "tsi-common-react/src/apps/reviews/containers/MultiProductWriteReviewForm"
        );
        const productUUIDs = elem.dataset.productUuids || "{}";
        const email = elem.dataset.email;
        return (
            <Provider store={store}>
                <MultiProductWriteReviewForm
                    reviewsInfoMapJSON={productUUIDs}
                    email={email}
                />
            </Provider>
        );
    },
).catch(console.error);

/**
 * Render ReviewsViewer SPA
 */
dynamicPlaceComponent('[data-place-react="review-spa"]', async (elem) => {
    const { ReviewsViewer } = await import(
        "tsi-common-react/src/apps/reviews/containers/ReviewsViewer"
    );
    const brandID = isoReviewsBrandID.wrap(
        parseInt(elem.dataset.reviewsBrandId || "1", 10),
    );
    const displayedProductTypeIDWhitelist = JSON.parse(
        elem.dataset.reviewsProductTypeWhitelist || "",
    );
    const defaultSortOption = (elem.dataset.defaultSort ||
        "-created_datetime") as ReviewSortOption;
    const pageSize = parseInt(elem.dataset.pageSize || "6", 10);
    const showSources =
        (elem.dataset.showSources || "True").toLowerCase() === "true";
    const writeReviewURL = elem.dataset.writeReviewUrl;
    const showAppliedFilters =
        (elem.dataset.showAppliedFilters || "false").toLowerCase() === "true";
    // TODO(i18n): Do not hardcode brand name in filter - should come from backend or be translatable
    const reviewSourceFilter = showSources ? [] : ["Tempur-Pedic"];
    // Update selected product types based on a given type whitelist
    if (displayedProductTypeIDWhitelist.length) {
        const selectedProductTypeIDs = store
            .getState()
            .reviews.ui.productTypeIDs.filter((tid) => {
                return displayedProductTypeIDWhitelist.includes(tid);
            });
        if (selectedProductTypeIDs.length <= 0) {
            selectedProductTypeIDs.push(displayedProductTypeIDWhitelist[0]);
        }
        store.dispatch(setProductTypes(selectedProductTypeIDs));
    }
    return (
        <Provider store={store}>
            <ReviewsViewer
                brandID={brandID}
                defaultSortOption={defaultSortOption}
                displayedProductTypeIDWhitelist={
                    displayedProductTypeIDWhitelist
                }
                pageSize={pageSize}
                showAppliedFilters={showAppliedFilters}
                showSources={showSources}
                sourceFilterWhitelist={reviewSourceFilter}
                starHasStroke={true}
                writeReviewURL={writeReviewURL}
            />
        </Provider>
    );
}).catch(console.error);

/**
 * Render PDP Reviews Application
 */
dynamicPlaceComponent('[data-place-react="product-reviews"]', async (elem) => {
    const { ProductReviews } = await import(
        "tsi-common-react/src/apps/reviews/containers/ProductReviews"
    );
    const brandID = isoReviewsBrandID.wrap(
        parseInt(elem.dataset.reviewsBrandId || "", 10),
    );
    const productUUIDs = JSON.parse(elem.dataset.reviewsProductUuids || "[]");
    const numReviews = parseInt(elem.dataset.numReviews || "", 10);
    const averageRating = parseFloat(elem.dataset.averageRating || "");
    const ugcCarouselData: IUGCCarouselData = JSON.parse(
        elem.dataset.ugcData || "{}",
    );
    const showProductsFilter = strToBool(
        elem.dataset.showProductsFilter || "no",
    );
    const showSourcesFilter = strToBool(elem.dataset.showSourcesFilter || "no");
    return (
        <Provider store={store}>
            <ProductReviews
                title={elem.dataset.reviewsTitle || ""}
                brandID={brandID}
                productUUIDs={productUUIDs}
                numReviews={numReviews}
                averageRating={averageRating}
                showWriteReviewButton={true}
                writeReviewFormUUID={isoFormUUID.wrap(uuidv4())}
                ugcCarouselDesc={ugcCarouselData.desc}
                ugcCarouselPosts={ugcCarouselData.posts}
                showProductsFilter={showProductsFilter}
                showSourcesFilter={showSourcesFilter}
                starHasStroke={true}
                isCollapsedView={true}
            />
        </Provider>
    );
}).catch(console.error);
